import googleAds from '/google-ads.svg';
import { useEffect, useState } from 'react';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useSetAtom, useAtomValue } from 'jotai';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw, AlertCircle, ArrowRightLeft, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { useGoogleLogin } from '@react-oauth/google';

import { useNavigate } from '@tanstack/react-router';
import { useUserProfile } from '@/contexts/user.context';
import { usePlatformConnections } from '@/hooks/usePlatformConnections';
import { useSelectGoogleAdsAccount } from '@/hooks/useSelectGoogleAdsAccount';
import {
  GOOGLE_SERVICES_STATUS_QUERY_KEY,
  googleAdsAccountsQueryOptions,
  googleIntegrationScopesQueryOptions,
  useSilentRefreshGoogleAdsAccountsMutationOptions,
} from '@/lib/query-options';
import { googleAdsAccountAtom, organizationDataAtom } from '@/store';
import { GoogleAdsAccount } from '@/lib/types';
import { GoogleAdsAccountList } from '../googleads-account-list';
import { connectGoogleIntegration, refreshGoogleAdsAccounts } from '@/lib/apis';

interface GoogleAdAccountSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: 'select' | 'transfer';
  campaignName?: string;
  currentGoogleAdsAccountId?: string;
  onAccountSelect?: (account: GoogleAdsAccount) => void;
}

export const GoogleAdAccountSelectionModal = ({ 
  isOpen, 
  onClose, 
  mode = 'select',
  campaignName,
  currentGoogleAdsAccountId,
  onAccountSelect
}: GoogleAdAccountSelectionModalProps) => {
  const { toast } = useToast();
  const { data: accounts = [], refetch: refetchAccounts } = useSuspenseQuery(googleAdsAccountsQueryOptions());
  const queryClient = useQueryClient();
  const { selectAccount } = useSelectGoogleAdsAccount();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [accountErrors, setAccountErrors] = useState<Array<{ account_id: string; error: string }>>([]);
  const [currencyError, setCurrencyError] = useState<string | null>(null);

  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const setGoogleAdsAccount = useSetAtom(googleAdsAccountAtom);
  const currentOrganization = useAtomValue(organizationDataAtom);

  const { user } = useUserProfile();
  const navigate = useNavigate();

  // Not-connected vs connected-but-zero-accounts are different problems with different fixes
  // (#5977 parity with the Meta modal): disconnected users get routed to Integrations to connect;
  // the refresh path only makes sense once a connection exists. googleConnected already folds in
  // the broken-token flag (#5935), so a revoked connection lands on the reconnect path too.
  const { googleConnected, isLoading: isLoadingConnections } = usePlatformConnections();
  const showNotConnected = mode === 'select' && !isLoadingConnections && !googleConnected;

  const handleGoToIntegrations = () => {
    onClose();
    navigate({ to: '/settings/account', hash: 'integrations' });
  };

  // Suppress all refresh controls while impersonating. The OAuth popup runs in the admin's
  // own Google session, so completing it would write the admin's refresh_token to the
  // impersonated user's UserAccount row (per the JWT-anchored connect endpoint from #4152)
  // and rebind that user's Google Ads accounts to the admin's Google identity. The right
  // path for a stale-accounts situation is to have the actual user sign in and refresh.
  const isImpersonating = !!user?.impersonating_email;

  // Silent TTL-bounded re-enumeration on every open (#4207): the backend decides whether
  // Google is actually called (at most once per TTL; never without a stored token, never on
  // a broken connection), so firing unconditionally costs one cheap authenticated request.
  // Deliberately NOT gated on isImpersonating: it uses the impersonated user's own stored
  // token, and an impersonation-operated account has no other re-sync path at all (the
  // #4193/#4207 Oficina incidents). Keyed on the isOpen transition because this component
  // stays mounted with the dialog closed.
  const { mutate: silentRefresh } = useSilentRefreshGoogleAdsAccountsMutationOptions();
  useEffect(() => {
    if (isOpen) {
      silentRefresh();
    }
  }, [isOpen, silentRefresh]);

  const handleAccountSelect = async (account: GoogleAdsAccount) => {
    setCurrencyError(null);
    try {
      // If in transfer mode and custom handler is provided, use it
      if (mode === 'transfer' && onAccountSelect) {
        onAccountSelect(account);
        return;
      }
      
      // Select mode: the shared hook persists the choice and updates every surface (#6668).
      await selectAccount(account);
      onClose();
    } catch (error) {
      console.error('❌ Google Ads account switch failed:', error);
      const message = error instanceof Error ? error.message : 'Error selecting account';
      setCurrencyError(message);
    }
  };

  useEffect(() => {
    if (accounts.length > 0 && mode === 'select') {
      // Find the account selected for this organization, not the globally selected one
      const orgSelectedAccountId = currentOrganization?.membership?.last_selected_googleads_account_id;
      const selected = orgSelectedAccountId 
        ? accounts.filter((account) => account.googleads_account_id === orgSelectedAccountId)
        : [];
      if (selected.length > 0) {
        setGoogleAdsAccount(selected[0]);
      }
    } else if (mode === 'select') {
      if (user && user.email) {
        // toast({
        //   title: 'No Google Ads Account',
        //   description: `${user.email} does not have a Google Ads account associated with it.`,
        // });
      } else {
        // toast({
        //   title: 'No Google Ads Account',
        //   description: 'Your account does not have a Google Ads account associated with it.',
        // });
      }
    }
  }, [accounts, mode, currentOrganization, user, setGoogleAdsAccount]);

  // Google OAuth login to refresh accounts. The scope comes from the backend (#6879), as on Integrations.
  const { data: consent } = useSuspenseQuery(googleIntegrationScopesQueryOptions());
  const refreshGoogleAdsConnection = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      setIsRefreshing(true);
      try {
        
        // Connect (or re-connect) the Google Ads integration to the *current* Glitch user. Crucially
        // this is NOT /google/auth — that endpoint is sign-in/sign-up and anchors to the OAuth
        // identity, which silently saves the Ads accounts under whoever the Google email maps to (a
        // different Glitch user, or a freshly-created one) and the caller's view then shows "0
        // accounts found." Issue #4152. /google/integrations/connect is JWT-anchored — same shape as
        // the Meta + HubSpot connect endpoints.
        // Through the API helpers, which send X-Organization-Id: the backend stores whatever Analytics and Tag
        // Manager access the same consent granted on that organization, and drops it without one (#6879).
        await connectGoogleIntegration(codeResponse.code);
        queryClient.invalidateQueries({ queryKey: GOOGLE_SERVICES_STATUS_QUERY_KEY });

        // Call the refresh endpoint to get account errors
        const refreshData = await refreshGoogleAdsAccounts();

        // Refetch the accounts to get the updated list
        await refetchAccounts();

        // Store account errors in state to display inline
        if (refreshData.errors && refreshData.errors.length > 0) {
          setAccountErrors(refreshData.errors);
          toast({
            title: 'Connection Refreshed',
            description: `Found ${refreshData.accounts_found} accessible account(s) out of ${refreshData.accounts_found + refreshData.errors.length} total. See errors below.`,
          });
        } else {
          setAccountErrors([]);
          toast({
            title: 'Connection Refreshed',
            description: `Found ${refreshData.accounts_found} Google Ads account(s). All accounts are accessible.`,
          });
        }
      } catch (error: any) {
        console.error('Error refreshing Google Ads connection:', error);
        toast({
          title: 'Refresh Failed',
          description: error.message || 'Failed to refresh Google Ads connection. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setIsRefreshing(false);
      }
    },
    scope: consent.scope,
    flow: 'auth-code',
    include_granted_scopes: false,
  });

  // Select-mode copy mirrors the Meta picker ("Meta Ads Account" / "Select which Meta ad
  // account this organization uses…") for cross-platform parity (#5936).
  const getTitle = () => {
    if (mode === 'transfer') {
      return 'Transfer Campaign';
    }
    return 'Google Ads Account';
  };

  const getDescription = () => {
    if (mode === 'transfer') {
      return `Transfer "${campaignName}" to a different Google Ads account. This will change the campaign's Google Ads account ownership.`;
    }
    return 'Select which Google Ads account this organization uses when creating campaigns.';
  };

  const getEmptyStateMessage = () => {
    if (mode === 'transfer') {
      return 'No other Google Ads accounts available for transfer.';
    }
    return 'No Google Ads accounts found';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-lg shadow-lg'>
        <DialogHeader className='p-2'>
          <div className='flex items-start justify-between gap-3'>
            <div className='flex-1 min-w-0'>
              <DialogTitle className='text-2xl font-semibold flex items-center gap-2'>
                {mode === 'transfer' ? (
                  <ArrowRightLeft className="h-6 w-6" />
                ) : (
                  <img src={googleAds} alt="Google Ads" className="w-6 h-6" />
                )}
                {getTitle()}
              </DialogTitle>
              <DialogDescription>
                {getDescription()}
              </DialogDescription>
            </div>
            {/* Refresh control. The larger empty-state button only renders when the user has zero
                accounts; users with some-but-stale accounts (Google has granted more since the last
                sync) need a path to re-sync without signing out. See issue #4193. Hidden while
                impersonating to prevent the admin's Google identity being written to the
                impersonated user's row. */}
            {!isImpersonating && !showNotConnected && (
              <Button
                variant='ghost'
                size='sm'
                onClick={() => refreshGoogleAdsConnection()}
                disabled={isRefreshing}
                title='Refresh accounts from Google'
                aria-label='Refresh Google Ads accounts'
                className='shrink-0 mt-1 mr-8'
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </Button>
            )}
          </div>
        </DialogHeader>
        <Separator className='my-2' />
        {/* Rendered whenever impersonating, not only in the empty state: a stale-but-populated
            list with no refresh button and no explanation was misdiagnosed as a permissions bug
            twice (#4207). The silent refresh above still runs (it uses the customer's own stored
            token); this covers the cases where it is skipped or fails. */}
        {isImpersonating && !showNotConnected && (
          <div className='px-2 pb-2'>
            <div
              className='flex items-start gap-2 border-l-4 p-3 text-sm'
              style={{ color: '#290BD9', backgroundColor: '#EDE9FE', borderColor: '#C4B5FD' }}
            >
              <AlertCircle className='h-4 w-4 shrink-0 mt-0.5' />
              <p>
                Refresh via Google sign-in is disabled while impersonating. The account list still
                updates from Google automatically; granting access to additional accounts requires
                the actual user to sign in.
              </p>
            </div>
          </div>
        )}
        {/* Currency mismatch / precondition error — rendered ABOVE the account
            list so a blocking reason is always visible and never clipped behind
            the scrollable list area. #4273 */}
        {currencyError && (
          <div className='px-2 pb-2'>
            <div className="bg-red-50 border-l-4 border-l-red-400 border-t border-r border-b border-red-400 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-900">
                  <p>{currencyError}</p>
                  <p className="mt-2">
                    <a href="/organizations" className="text-[#290BD9] hover:text-[#2309C5] underline font-medium">Go to Organisation Settings</a> to update your currency.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        <Card className='border-none shadow-none'>
          <CardContent className='p-2 space-y-6 rounded-xl'>
            {showNotConnected ? (
              <div className='text-center py-8 space-y-4'>
                <div className='text-muted-foreground space-y-2'>
                  <p className='text-lg font-medium'>Google Ads Not Connected</p>
                  <p className='text-sm'>
                    Connect a Google account in Settings → Integrations to pick an ad account here.
                  </p>
                </div>
                <Button onClick={handleGoToIntegrations} className='mt-4 bg-[#290BD9] hover:bg-[#2309C5] text-white'>
                  Go to Integrations
                  <ExternalLink className='ml-2 h-4 w-4' />
                </Button>
              </div>
            ) : (
            <GoogleAdsAccountList
              accounts={accounts}
              selectedAccountId={mode === 'select' ? googleAdsAccount?.googleads_account_id : undefined}
              onSelect={handleAccountSelect}
              excludeAccountId={mode === 'transfer' ? currentGoogleAdsAccountId : undefined}
              listClassName={`bg-slate-100 rounded-xl p-2 ${currencyError ? 'max-h-[44vh]' : 'max-h-[60vh]'} overflow-y-auto overflow-x-clip space-y-1`}
              renderEmptyState={(searchQuery, hasAccounts) => (
                <div className='text-center py-8 space-y-4'>
                  {!hasAccounts ? (
                    <>
                      <div className='text-muted-foreground space-y-2'>
                        <p className='text-lg font-medium'>{getEmptyStateMessage()}</p>
                        {mode === 'select' && (
                          <p className='text-sm'>
                            We couldn't find any Google Ads accounts linked to your account ({user?.email}).
                          </p>
                        )}
                        {mode === 'transfer' && (
                          <p className='text-sm'>
                            You need at least one other Google Ads account to transfer this campaign.
                          </p>
                        )}
                        {/* The impersonation explanation now lives in the always-visible notice
                            above the list (#4207), so the empty state only adds the refresh hint
                            for users who can actually use the button. */}
                        {!isImpersonating && (
                          <p className='text-sm'>
                            If you have Google Ads accounts, try refreshing your connection.
                          </p>
                        )}
                      </div>
                      {!isImpersonating && (
                        <Button
                          onClick={() => refreshGoogleAdsConnection()}
                          disabled={isRefreshing}
                          className='mt-4'
                        >
                          {isRefreshing ? (
                            <>
                              <RefreshCw className='mr-2 h-4 w-4 animate-spin' />
                              Refreshing...
                            </>
                          ) : (
                            <>
                              <RefreshCw className='mr-2 h-4 w-4' />
                              Refresh Google Ads Connection
                            </>
                          )}
                        </Button>
                      )}
                    </>
                  ) : (
                    <div className='text-muted-foreground'>
                      <p>No accounts found matching "{searchQuery}"</p>
                    </div>
                  )}
                </div>
              )}
            />
            )}
          </CardContent>
        </Card>
        
        {/* Display account errors outside the grey box using standard error styling */}
        {accountErrors.length > 0 && (
          <div className='px-2 pb-2 space-y-3'>
            {accountErrors.map((error, index) => (
              <div key={index} className="bg-red-50 border-l-4 border-l-red-400 border-t border-r border-b border-red-400 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-gray-900">
                    <strong>Account {error.account_id}:</strong> {error.error}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
